/**
 * ClockworkCanopy -- Level 3.
 *
 * Redwood peaks with clockwork observatories, giant glass lenses, star charts.
 * Theme: Integration and Harmony -- final level requiring mastery of all
 * abilities plus both co-op powers (Resonant Drop + Chronos Bubble).
 *
 * Puzzles (in order) -- each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/clockwork-canopy/`:
 *   1. Gear Alignment  -- `GearAlignmentPuzzle`
 *   2. Light Path      -- `LightPathPuzzle`
 *   3. Timed Resonance -- `TimedResonancePuzzle`
 *   4. Shadow Relay    -- `ShadowRelayPuzzle`
 *   5. The Orrery      -- `TheOrreryPuzzle` (apex — gates on the other four)
 *
 * This file is now the scene shell only: boot, camera, lighting,
 * post-processing, environment art, the Elder encounter, and level
 * completion. All puzzle geometry + logic lives in the puzzle modules.
 *
 * Elder Leo returns for the final encounter, teaching the Wisdom of
 * Integration. The Elder cutscene is triggered by `PORTAL_ENTERED` once
 * the apex puzzle activates the exit portal.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { applyVisualProfile, CLOCKWORK_CANOPY_PROFILE } from "../levels";
import {
  ParallaxBackground,
  paintGradient,
  paintStarfield,
  paintClockworkSilhouette,
} from "../systems/ParallaxBackground";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";
import { getAssetManager, InstantiatedModel } from "../systems/AssetManager";
import { freezeStaticMeshes, DEFAULT_STATIC_EXCLUDES, scatterInstances, type ScatterPlacement } from "../levels/staticOptimization";

import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { ChronosBubble } from "../abilities/coop/ChronosBubble";
import { ElderLeo } from "../entities/npcs/ElderLeo";
import { GreaseCache } from "../entities/interactives/GreaseCache";

import { CLOCKWORK_CANOPY, COLORS, WORLD_DEBUG } from "../config/GameConfig";
import { tagAs } from "../levels";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { ELDER_LEO_CANOPY_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import { CLOCKWORK_CANOPY_PLATFORMS, platformDepth, platformHeight } from "../levels/layouts";
import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { GearAlignmentPuzzle } from "../levels/puzzles/clockwork-canopy/GearAlignment";
import { LightPathPuzzle } from "../levels/puzzles/clockwork-canopy/LightPath";
import { TimedResonancePuzzle } from "../levels/puzzles/clockwork-canopy/TimedResonance";
import { ShadowRelayPuzzle } from "../levels/puzzles/clockwork-canopy/ShadowRelay";
import { TheOrreryPuzzle } from "../levels/puzzles/clockwork-canopy/TheOrrery";
import { LevelCueDriver } from "../levels/LevelCueDriver";

// ─── Types ──────────────────────────────────────────────────────────

export interface ClockworkCanopyContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Clockwork Canopy scene.
 * Call after the engine and scene are created.
 */
export async function setupClockworkCanopy(scene: Scene): Promise<ClockworkCanopyContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "ClockworkCanopy",
    clearColor: new Color4(0.02, 0.04, 0.1, 1.0),
    fogColor: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    fogDensity: 0.008,
    // Spawn staggered around plat_s1_1's center (x=7, width=12, spans
    // x∈[1, 13]). Old spawn at x=5 sat 4u from the west edge — pressing
    // `d` (screen-right = world-negative-X) walked the cat off the
    // platform in <1s. New spawns at x=6 and x=8 give each cat ~5u
    // margin both directions; a reflex tap either way is still safe.
    minnaSpawn: new Vector3(6, 4, 0),
    basilSpawn: new Vector3(8, 4, 0),
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, CLOCKWORK_CANOPY_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // --- Clockwork-specific bioluminescent SpotLight ---
  // The AccentLightPreset type only supports PointLight, so this cool
  // blue canopy accent remains inline. It gives the treetops a single
  // focused beam in contrast to the warm brass accents.
  const bioLight = new SpotLight(
    "bioCanopy",
    new Vector3(60, 15, -2),
    new Vector3(0, -1, 0.3),
    Math.PI / 3,
    2,
    scene,
  );
  bioLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE);
  bioLight.intensity = 0.5;
  bioLight.range = 20;

  // --- Parallax cosmic depth (Phase C3) ---
  // Canopy is the finale — it should read as a cathedral of sky. Three
  // layers: deep starfield (emissive), a band of distant redwood
  // silhouettes, and far-mid constellation scatter for extra depth.
  const canopyParallax = new ParallaxBackground(scene, cameraSystem.camera, [
    {
      // Cosmos + gradient shifted to *wider* scrollFactor spread so the
      // parallax differential actually reads as depth when the camera
      // pans. Gemini 3.1-pro flagged prior 0.98 / 0.95 / 0.45 tuning as
      // "single flat background." New spread: 0.92 / 0.7 / 0.3 — the
      // peak band now slides 62% slower than the cosmos.
      name: "canopyCosmos",
      z: -60,
      scrollFactor: 0.92,
      width: 300,
      height: 90,
      yOffset: 18,
      alpha: 1.0,
      emissiveOnly: true,
      paint: paintStarfield(0.0008, 0xA11A),
    },
    {
      name: "canopyDeepGradient",
      z: -50,
      scrollFactor: 0.7,
      width: 260,
      height: 70,
      yOffset: 12,
      alpha: 0.9,
      paint: paintGradient("#020214", "#0a1030"),
    },
    {
      // Replaced the tree-silhouette layer with brass clockwork gear
      // outlines. Tree silhouettes on the L3 cosmic backdrop read as
      // "wrong level / forest" per user feedback — canon is a starlit
      // observatory cathedral, not a forest horizon.
      name: "canopyDistantGears",
      z: -22,
      scrollFactor: 0.3,
      width: 220,
      height: 30,
      yOffset: 10,
      // Q-039: lifted off the gradient once ParallaxBackground stopped
      // rendering painted layers as black cutouts. #241633 is a deep
      // violet ~2.5x the "#020214"→"#0a1030" gradient's luminance — the
      // gears now read as silhouettes against the cosmos, not features.
      alpha: 0.65,
      paint: paintClockworkSilhouette("#241633", 10, 0xC70C1E),
    },
  ]);
  base.addDisposeCallback(
    base.registerSafeBeforeRender("canopyParallax", () => canopyParallax.update()),
  );
  base.addDisposeCallback(() => canopyParallax.dispose());

  // --- Environment ---
  // Primary art: Blender-authored `clockwork_canopy_env.glb` — redwood
  // peak silhouettes, wooden observatory decks with brass rims and
  // tilted lens discs, the apex ornamental gear, starlit backdrop.
  // Procedural fallback (`createBackground` + `createStarfield`) still
  // runs to fill in foreground beams, platform silhouettes, and the
  // emissive in-engine stars that sell the lighting cue.
  let envInstance: InstantiatedModel | null = null;
  try {
    envInstance = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/clockwork_canopy_env.glb",
    );
    for (const mesh of envInstance.meshes) {
      mesh.isPickable = false;
      if (mesh.name !== "__root__" && !mesh.name.startsWith("__root__")) {
        // Environment meshes RECEIVE shadows but must not CAST them:
        // the directional shadow map's ortho frustum auto-fits to the
        // caster set, and registering the whole env GLB (incl. the
        // 170u sky backdrop) stretched one map over the entire level —
        // cat shadows degraded to blobs (2026-07-10 review, finding #3).
        mesh.receiveShadows = true;
      }
    }
    // Replace the authored `sky_backdrop` plane with a world-space
    // procedural one. The shipped GLB authored the backdrop as a
    // 170u-wide plane centered at x=-70 (right edge at x=15), but the
    // canopy level extends east to ~x=160. Every frame east of x=15
    // showed a hard vertical seam where the authored backdrop ended
    // and only the (visually different) parallax cosmos plane covered.
    // P0 finding in the 2026-04-18 visual review.
    //
    // The original mesh is disposed (freeing its unique material +
    // the imported GLB clone) and a fresh `canopy_sky_backdrop` plane
    // is created in world space covering the full playable range,
    // inheriting the authored starlit_sky material's color spec.
    for (const mesh of envInstance.meshes) {
      if (!/sky_backdrop/i.test(mesh.name)) continue;
      mesh.setEnabled(false);
      mesh.isVisible = false;
    }
    const procSky = MeshBuilder.CreatePlane(
      "canopy_sky_backdrop",
      { width: 240, height: 55 },
      scene,
    );
    procSky.position = new Vector3(80, 0, -22);
    procSky.isPickable = false;
    procSky.checkCollisions = false;
    procSky.receiveShadows = false;
    procSky.renderingGroupId = 0;
    const procSkyMat = new StandardMaterial("canopy_sky_backdrop_mat", scene);
    // Match the authored starlit_sky color (PBR albedo 0.04/0.06/0.18,
    // emissive 0.11/0.18/0.40 — slightly blue star-glow). Use
    // StandardMaterial for a cheaper unlit look since the backdrop is
    // scenery, not a PBR surface.
    procSkyMat.diffuseColor = new Color3(0.04, 0.06, 0.18);
    procSkyMat.emissiveColor = new Color3(0.11, 0.18, 0.40);
    procSkyMat.specularColor = Color3.Black();
    procSkyMat.disableLighting = true;
    // Leave the replacement backdrop translucent so the parallax cosmos
    // and clockwork silhouettes behind it read as depth instead of a flat
    // blue wall in the L3 gameplay captures.
    procSkyMat.alpha = 0.68;
    procSkyMat.backFaceCulling = false;
    procSky.material = procSkyMat;
    base.addDisposeCallback(() => {
      procSky.dispose();
      procSkyMat.dispose();
    });
    // Freeze world matrices + materials on the env GLB. The Canopy env
    // is pure scenery (redwood peaks, observatory decks, ornamental
    // gear, star nodes) — nothing mutates at runtime.
    freezeStaticMeshes(envInstance.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
    base.addDisposeCallback(() => envInstance?.dispose());
  } catch (err) {
    console.warn("[ClockworkCanopy] env GLB load failed, falling back to procedural:", err);
  }
  // Phase C3 + C5 replaced both inline background helpers with the
  // painted ParallaxBackground layers above (deep cosmos gradient +
  // starfield + distant peaks) plus cosmic-shimmer particles. The old
  // `createBackground` drew an opaque `skyBackdrop` plane at z=-10 that
  // occluded the parallax entirely; `createStarfield` was a static
  // point-emissive soup not tied to camera parallax.
  // createBackground(scene);
  // createStarfield(scene);

  // --- Meshy-generated clockwork observatory props ---
  // Brass observatory platforms with lenses + gears, scattered at key
  // sightline positions. Generated 2026-04-16, 138MB→4.4MB optimized.
  const OBSERVATORY_PLACEMENTS = [
    { x: 12, y: 8, z: -4, yaw: 0.1, scale: 0.8 },    // near gear A
    { x: 20, y: 10, z: -5, yaw: -0.2, scale: 0.7 },   // near gear B
    { x: 50, y: 9, z: -5, yaw: 0.15, scale: 0.9 },    // mid-level
    { x: 125, y: 10, z: -4, yaw: -0.1, scale: 1.0 },  // apex/orrery
  ];
  const obsScatter = await scatterInstances(
    scene,
    "/assets/models/environment/clockwork_observatory.glb",
    OBSERVATORY_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "clockworkObs" },
  );
  base.addDisposeCallback(() => obsScatter.dispose());

  // --- Meshy L3 hero props (2026-04-17) ----------------------------
  // Four props authored via Meshy text-to-3D (PBR): lens pedestals for
  // the Light Path puzzle, constellation beacons for The Orrery sync
  // plates, stained-glass dome segments ringing the apex, and suspended
  // clockwork rigging as atmosphere. Each is loaded + scattered with
  // freezeStaticMeshes for zero per-frame matrix cost. All fall back
  // silently (scatterInstances logs a warn) if the GLB fails to load.

  // Lens pedestals sit behind the z=0 player/puzzle plane, directly
  // under the procedural lens pedestal cylinders authored in
  // LightPath.ts (x=35/40/45). Y is -0.5 from the pedestal top so the
  // Meshy dish cradle lines up with the procedural rim. Scale 0.5 —
  // Meshy outputs at ~2m, pedestal needs ~1m tall.
  const LENS_PEDESTAL_PLACEMENTS = [
    { x: 35, y: 5.3, z: -0.6, yaw: 0.0, scale: 0.5 },
    { x: 40, y: 7.3, z: -0.6, yaw: 0.3, scale: 0.5 },
    { x: 45, y: 9.3, z: -0.6, yaw: -0.2, scale: 0.5 },
  ];
  const lensPedestalScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l3_lens_pedestal.glb",
    LENS_PEDESTAL_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l3LensPedestal" },
  );
  base.addDisposeCallback(() => lensPedestalScatter.dispose());

  // Constellation beacons — one per lowered sync plate in TheOrrery
  // puzzle (x=120, x=130 at y=5.0). Beacons sit just behind and above
  // the plates (z=-0.6) so the cats can still walk onto the plates
  // without the beacon blocking the path. A cyan emissive tint is
  // applied post-load so the beacons glow even if Meshy's PBR pass
  // dialed emissive down.
  const CONSTELLATION_BEACON_PLACEMENTS = [
    { x: 120, y: 5.4, z: -0.6, yaw: 0.2, scale: 0.6 },
    { x: 130, y: 5.4, z: -0.6, yaw: -0.2, scale: 0.6 },
  ];
  const constellationBeaconScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l3_constellation_beacon.glb",
    CONSTELLATION_BEACON_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l3ConstellationBeacon" },
  );
  base.addDisposeCallback(() => constellationBeaconScatter.dispose());

  // Layer a cold-cyan emissive tint onto the beacon PBR materials so the
  // zodiac glyphs visibly glow against the deep midnight apex. Meshy's
  // refine pass gives us roughness + metallic but typically zeroes
  // emissive; a subtle additive tint is safe because the materials are
  // frozen (we unfreeze, patch, freeze is overkill — emissiveColor is a
  // shader uniform, not a matrix, so it applies even on frozen mats).
  const beaconCyan = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION).scale(0.6);
  for (let i = 0; i < constellationBeaconScatter.instances.length; i++) {
    const inst = constellationBeaconScatter.instances[i];
    for (const mesh of inst.meshes) {
      const mat = mesh.material;
      if (mat instanceof PBRMaterial) {
        mat.emissiveColor = beaconCyan;
        mat.emissiveIntensity = 1.2;
      }
    }
    // Anchor a small cyan point-light inside each beacon so the cold
    // emissive reads in the scene's darker corners as well. Read the
    // scatter wrapper's position (world anchor) — `inst.rootNodes[0]`
    // is the untouched glTF `__root__` at (0,0,0) after the wrapper-
    // based scatter refactor; the wrapper parent carries the world
    // placement.
    const wrapper = constellationBeaconScatter.wrappers[i];
    const rootPos = wrapper?.position;
    if (rootPos) {
      const beaconLight = new PointLight(
        `l3_beaconLight_${rootPos.x | 0}`,
        new Vector3(rootPos.x, rootPos.y + 1.6, rootPos.z),
        scene,
      );
      beaconLight.diffuse = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION);
      beaconLight.intensity = 0.55;
      beaconLight.range = 6;
      base.addDisposeCallback(() => beaconLight.dispose());
    }
  }

  // Observatory dome segments — ring of ~10 wedges assembled into a
  // half-dome over the apex/orrery platform (center x=125). Each wedge
  // is rotated by its angular index so the brass frames line up like
  // pie slices. Positioned at y=15 (well above gameplay) with z=-3 so
  // the dome reads as a background silhouette rather than a collider.
  const DOME_CENTER = new Vector3(125, 15, -3);
  const DOME_RADIUS = 7;
  const DOME_SEGMENT_COUNT = 10;
  const domePlacements: ScatterPlacement[] = [];
  for (let i = 0; i < DOME_SEGMENT_COUNT; i++) {
    // Cover 210° from -105° to +105° around the apex, skewed so the
    // segments face the camera; the remaining 150° wrap behind the
    // apex where the camera never looks.
    const t = i / (DOME_SEGMENT_COUNT - 1);
    const angle = -Math.PI * 0.58 + t * Math.PI * 1.16;
    domePlacements.push({
      x: DOME_CENTER.x + Math.sin(angle) * DOME_RADIUS,
      y: DOME_CENTER.y + Math.cos(angle) * 2.0,
      z: DOME_CENTER.z - Math.abs(Math.sin(angle)) * 1.5,
      yaw: angle,
      scale: 0.9,
    });
  }
  const domeScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l3_observatory_dome_segment.glb",
    domePlacements,
    { shadowGenerator: shadowGen, tag: "l3DomeSegment" },
  );
  base.addDisposeCallback(() => domeScatter.dispose());

  // Clockwork brass rigging — four suspended chain/gear bundles hanging
  // from the ceiling at atmospheric hand-off points. Placed slightly
  // in front of the backdrop (z=-2.2) so they read against the parallax
  // starfield without occluding puzzle meshes.
  //
  // NOTE: `l3_clockwork_rigging.glb` is a Blender MCP procedural stub —
  // the Meshy refine for this prop ran out of shared API credits during
  // the 2026-04-17 generation run (190→0 in a concurrent L2 agent). The
  // stub is three interlocking gears on a chain bundle with a pendulum
  // weight, readable as "brass clockwork" in silhouette. TODO: rerun
  // Meshy refine + pbr when credits refresh.
  const RIGGING_PLACEMENTS = [
    { x: 18, y: 13, z: -2.2, yaw: 0.0, scale: 0.9 },
    { x: 55, y: 14, z: -2.2, yaw: 0.4, scale: 0.85 },
    { x: 90, y: 13, z: -2.2, yaw: -0.3, scale: 0.95 },
    { x: 125, y: 14, z: -2.2, yaw: 0.15, scale: 1.0 },
  ];
  // No `shadowGenerator` here (Q-039): overhead scenery at y=13-14
  // should not stretch the caster frustum — dropping it shrinks L3's
  // shadow-caster bounds (same trim rationale as commit cd1202f).
  const riggingScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l3_clockwork_rigging.glb",
    RIGGING_PLACEMENTS,
    { tag: "l3ClockworkRigging" },
  );
  base.addDisposeCallback(() => riggingScatter.dispose());

  // Material patch (Q-039): the stub's single `l3_rigging_brass` PBR is
  // metallic 0.9 / roughness 0.3 with no textures and no environment
  // reflections — under the STARLIT_CANOPY rig that renders near-black
  // ("blurry black square" x4). Knock the metal down, roughen it, and
  // lift it with a low warm-brass emissive so the gears read as brass
  // silhouettes. Uniform-only writes are safe on frozen materials
  // (mirrors the constellation-beacon precedent above).
  const riggingBrass = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM).scale(0.30);
  for (const inst of riggingScatter.instances) {
    for (const mesh of inst.meshes) {
      const mat = mesh.material;
      if (mat instanceof PBRMaterial) {
        mat.metallic = 0.45;
        mat.roughness = 0.40;
        mat.emissiveColor = riggingBrass;
        mat.emissiveIntensity = 1.2;
      }
    }
  }

  // Slow rotation hint on rigging wrapper nodes so the overhead
  // clockwork feels alive. Rotation is on the Y axis at <0.1 rad/s; the
  // frozen child meshes update cheaply because only the wrapper
  // TransformNode matrix is dirty each frame.
  //
  // We rotate the scatter wrapper (world-placed parent) rather than the
  // untouched glTF `__root__`: the glTF root still carries the
  // right→left handedness quaternion (0,1,0,0), so writing to
  // `rootNodes[0].rotation.y` would be ignored (Euler rotation is
  // silently bypassed when `rotationQuaternion` is set).
  const riggingRoots = riggingScatter.wrappers;
  if (riggingRoots.length > 0) {
    base.addDisposeCallback(
      base.registerSafeBeforeRender("canopyRiggingSpin", () => {
        const t = performance.now() / 1000;
        for (let i = 0; i < riggingRoots.length; i++) {
          // Each rigging cluster rotates at a slightly different rate so
          // they don't all pirouette in lockstep.
          riggingRoots[i].rotation.y = (t * (0.04 + i * 0.015)) % (Math.PI * 2);
        }
      }),
    );
  }

  // --- C5: Orrery cathedral atmosphere ------------------------------
  // The apex is the game's finale — the bar from book p.25-28 is
  // "majestic, lucid presence." Three atmosphere adds here:
  //   (a) A slow cosmic-shimmer particle emitter above the orrery
  //       platform so the Tuning Bubble area reads as "sky in a dome."
  //   (b) A warm brass glow-ramp light pinned to the orrery platform —
  //       pulses gently between 0.6 and 1.4 intensity so the brass
  //       reflection doesn't sit flat.
  //   (c) A procedural lion-head pedestal at the west edge of the apex
  //       platform matching the stone lion behind Leo on p.25.

  const cosmicShimmer = new ParticleSystem("canopyCosmicShimmer", 260, scene);
  cosmicShimmer.emitter = new Vector3(125, 11, -1.5);
  cosmicShimmer.minEmitBox = new Vector3(-8, -2, -2);
  cosmicShimmer.maxEmitBox = new Vector3(8, 3, 2);
  cosmicShimmer.minLifeTime = 3.5;
  cosmicShimmer.maxLifeTime = 7.5;
  cosmicShimmer.minSize = 0.04;
  cosmicShimmer.maxSize = 0.14;
  cosmicShimmer.emitRate = 40;
  cosmicShimmer.minEmitPower = 0.05;
  cosmicShimmer.maxEmitPower = 0.25;
  cosmicShimmer.color1 = new Color4(0.7, 0.85, 1.0, 0.9);
  cosmicShimmer.color2 = new Color4(0.9, 0.75, 1.0, 0.7);
  cosmicShimmer.colorDead = new Color4(0.05, 0.05, 0.2, 0);
  cosmicShimmer.blendMode = ParticleSystem.BLENDMODE_ADD;
  cosmicShimmer.direction1 = new Vector3(-0.3, -0.05, -0.3);
  cosmicShimmer.direction2 = new Vector3(0.3, 0.2, 0.3);
  cosmicShimmer.start();
  base.addDisposeCallback(() => cosmicShimmer.dispose());

  const orreryGlow = new PointLight(
    "orreryBrassGlow",
    new Vector3(125, 6.5, -0.4),
    scene,
  );
  orreryGlow.diffuse = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM);
  orreryGlow.specular = new Color3(1.0, 0.8, 0.4);
  orreryGlow.intensity = 0.8;
  orreryGlow.range = 12;
  base.addDisposeCallback(() => orreryGlow.dispose());
  // Ramp the brass glow on a slow sin so the orrery looks like a warm
  // breathing mechanism rather than a static set-piece.
  base.addDisposeCallback(
    base.registerSafeBeforeRender("canopyOrreryGlow", () => {
      const t = performance.now() / 1000;
      orreryGlow.intensity = 0.8 + Math.sin(t * 0.9) * 0.35;
    }),
  );

  // Procedural lion-head pedestal (low-poly stand-in for a future GLB).
  // Two stacked blocks for the pedestal + a sphere head + two ear cones.
  // Tagged placeholder so the asset audit shows it as a known stub.
  const lionRoot = new TransformNode("canopy_lionPedestal", scene);
  lionRoot.position = new Vector3(118, 5.55, -1.6);
  {
    const plinthMat = new PBRMaterial("canopy_lion_plinth_mat", scene);
    plinthMat.albedoColor = new Color3(0.62, 0.58, 0.5);  // cool limestone
    plinthMat.metallic = 0.0;
    plinthMat.roughness = 0.82;
    plinthMat.emissiveColor = new Color3(0.04, 0.04, 0.05);

    const plinth = MeshBuilder.CreateBox("lion_plinth", { width: 1.4, height: 1.2, depth: 1.0 }, scene);
    plinth.parent = lionRoot;
    plinth.position.y = 0.6;
    plinth.material = plinthMat;
    shadowGen.addShadowCaster(plinth);

    const cap = MeshBuilder.CreateBox("lion_cap", { width: 1.6, height: 0.22, depth: 1.15 }, scene);
    cap.parent = lionRoot;
    cap.position.y = 1.3;
    cap.material = plinthMat;
    shadowGen.addShadowCaster(cap);

    const headMat = new PBRMaterial("canopy_lion_head_mat", scene);
    headMat.albedoColor = new Color3(0.7, 0.64, 0.55);
    headMat.metallic = 0.0;
    headMat.roughness = 0.78;
    headMat.emissiveColor = new Color3(0.05, 0.04, 0.03);

    const head = MeshBuilder.CreateSphere("lion_head", { diameter: 0.95, segments: 12 }, scene);
    head.parent = lionRoot;
    head.position = new Vector3(0, 1.9, 0);
    head.scaling = new Vector3(1.0, 0.95, 1.1);
    head.material = headMat;
    shadowGen.addShadowCaster(head);

    for (const side of [-1, 1]) {
      const ear = MeshBuilder.CreateCylinder(
        `lion_ear_${side > 0 ? "r" : "l"}`,
        { diameterBottom: 0.32, diameterTop: 0.05, height: 0.3, tessellation: 12 },
        scene,
      );
      ear.parent = lionRoot;
      ear.position = new Vector3(side * 0.28, 2.25, 0.0);
      ear.rotation.z = side * 0.2;
      ear.material = headMat;
      shadowGen.addShadowCaster(ear);
    }

    const snout = MeshBuilder.CreateSphere("lion_snout", { diameter: 0.38, segments: 10 }, scene);
    snout.parent = lionRoot;
    snout.position = new Vector3(0, 1.78, 0.45);
    snout.scaling = new Vector3(0.95, 0.7, 0.9);
    snout.material = headMat;
    shadowGen.addShadowCaster(snout);
  }
  base.addDisposeCallback(() => lionRoot.dispose(false, true));

  // --- Ground & Platforms ---
  createGroundAndPlatforms(scene, physicsSystem, shadowGen);

  // Add players as shadow casters
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);

  // --- Shadow zones ---
  const shadowZones = createShadowZones(scene);
  shadowSlip.registerShadowZones(shadowZones);

  // --- Co-op abilities (both available from Level 3 start) ---
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh);
  const chronosBubble = new ChronosBubble(scene, basil.mesh, minna.mesh);
  basilAbilities.addAbility(resonantDrop);
  basilAbilities.addAbility(chronosBubble);

  // --- Constellation accent lights (used by both the background and the apex puzzle) ---
  const constellationLights: PointLight[] = [];
  const constellationPositions = [
    new Vector3(30, 25, -5), new Vector3(60, 28, -5),
    new Vector3(90, 26, -5), new Vector3(120, 30, -5),
    new Vector3(45, 30, -5), new Vector3(75, 32, -5),
    new Vector3(105, 27, -5), new Vector3(135, 29, -5),
  ];
  for (let i = 0; i < constellationPositions.length; i++) {
    const star = new PointLight(`constellation_${i}`, constellationPositions[i], scene);
    star.diffuse = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION);
    star.intensity = 0.3;
    star.range = 15;
    constellationLights.push(star);
  }

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:  0-25   (Puzzle 1 - Gear Alignment)
  //   Section 2: 25-55   (Puzzle 2 - Light Path)
  //   Section 3: 55-80   (Puzzle 3 - Timed Resonance)
  //   Section 4: 80-110  (Puzzle 4 - Shadow Relay)
  //   Section 5: 110-140 (Apex - The Orrery + Elder)

  const orrery = new TheOrreryPuzzle(constellationLights);
  const puzzles: PuzzleModule[] = [
    new GearAlignmentPuzzle(),
    new LightPathPuzzle(),
    new TimedResonancePuzzle(),
    new ShadowRelayPuzzle(),
    orrery,
  ];

  // Level-scoped shared puzzle state (read by the apex puzzle to gate on
  // the other four). Each puzzle writes into this map before the game loop
  // tick by way of the wrapper below.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we pass the full list to the
  // ability once setup is complete.
  const allInteractives: InteractiveInfo[] = [];

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
  };

  // Setup all puzzles. Done sequentially so async work (model loads) runs
  // in order — each puzzle returns quickly since nothing loads at runtime.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    // Seed the state map with the initial state so apex gating can read it
    // immediately (puzzles that report `solved: false` initially are fine).
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Register AuraSight interactives and the gear connection line.
  auraSight.registerInteractives(allInteractives);
  // Connection line: gearA -> gearB (first two interactives registered).
  if (allInteractives.length >= 2) {
    auraSight.registerConnections([[allInteractives[0], allInteractives[1]]]);
  }

  // ─── Elder Leo (hidden until encounter) ──────────────────────────

  const elderLeoPos = new Vector3(130, 12, 0);
  const elderLeo = new ElderLeo(scene, elderLeoPos);
  elderLeo.mesh.setEnabled(false);
  shadowGen.addShadowCaster(elderLeo.mesh);
  base.addDisposeCallback(() => elderLeo.dispose());

  // ── Making Biscuits anchor (Leo's green argyle sweater) ──
  // Per SPEC-making-biscuits.md §2: "Leo's green argyle sweater, draped
  // over a parlor chair." Placed near the Elder's spawn so Minna finds
  // it during the finale's contemplative beat. Hidden by default —
  // revealed via Aura Sight.
  const leoBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "leo",
    minna,
    cutsceneSystem,
    position: new Vector3(elderLeoPos.x - 4, elderLeoPos.y - 0.4, 0.2),
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => leoBiscuit.dispose());

  // ─── Composite puzzle handle for QA tooling parity ───────────────
  const compositePuzzle = {
    id: "clockworkCanopyComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      gearAlignment: puzzles[0],
      lightPath: puzzles[1],
      timedResonance: puzzles[2],
      shadowRelay: puzzles[3],
      theOrrery: puzzles[4],
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness so
  // `scene.metadata.level.getQAHints()` can aggregate across them.
  // Same wire-up OvergrownConservatory does for the hybrid primitives.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // L3 introduces no new abilities, so each puzzle's getCue() names the
  // ability its current step needs; the driver pins it to the thing to
  // act on, with an "(N/5)" objective line. See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Align the brass gears" },
      { puzzle: puzzles[1], objective: "Light the lens path" },
      { puzzle: puzzles[2], objective: "Slip through the timed gate" },
      { puzzle: puzzles[3], objective: "Charge the shadow relay" },
      { puzzle: puzzles[4], objective: "Awaken the Orrery" },
    ],
    gameEvents,
    // The awakened Orrery opens the portal; the Elder waits beyond it.
    () => (levelComplete ? null : orrery.getPortalCue()),
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Level state (shell only) ────────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;

  // ─── Portal entry triggers the Elder encounter ───────────────────
  base.addUnsub(gameEvents.on(GameEvents.PORTAL_ENTERED, () => {
    if (levelComplete) return;
    startElderEncounter();
  }));

  function startElderEncounter(): void {
    if (elderEncounterDone) return;
    elderEncounterDone = true;

    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "leo",
      level: "ClockworkCanopy",
    });

    const dialogueSteps: CutsceneStep[] = ELDER_LEO_CANOPY_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    // Canon halo-merge beat (book p. 28): "For the first time in the
    // game, Basil does not have to stand outside the Tuning Bubble
    // to hold a lever — the machinery sustains itself. He steps into
    // the ethereal projection alongside Minna. The boundary between
    // hardware and software — between who moves and who watches — is
    // dissolved."
    //
    // We deliver this without a new CutsceneStep type by scripting a
    // pan across Minna → Basil, a callback that teleports Basil onto
    // Minna's spot (so the silhouettes overlap), and a glow-layer
    // intensity bump to sell the halo merge. The closing stanza then
    // plays over the merged frame.
    const minnaMesh = minna.mesh;
    const basilMesh = basil.mesh;
    let savedBasilPos: Vector3 | null = null;
    const mergeTarget = { x: elderLeoPos.x - 2, y: elderLeoPos.y - 1, z: 0 };

    const steps: CutsceneStep[] = [
      { type: "fade", fadeDirection: "out", duration: 800 },
      { type: "callback", fn: () => {
        elderLeo.mesh.setEnabled(true);
      }},
      { type: "fade", fadeDirection: "in", duration: 800 },
      { type: "pan", target: { x: elderLeoPos.x, y: elderLeoPos.y }, duration: 2000 },
      { type: "wait", duration: 500 },
      // Dialogue plays with cats in their apex positions
      ...dialogueSteps,
      // Halo-merge gesture — during the final shake, Basil physically
      // steps into Minna's ethereal projection. We cache his original
      // position so nothing downstream breaks if the cutscene is ever
      // replayed (level-complete disposes the scene shortly after).
      { type: "shake", duration: 800, intensity: 0.005 },
      { type: "callback", fn: () => {
        savedBasilPos = basilMesh.position.clone();
        basilMesh.position.x = minnaMesh.position.x;
        basilMesh.position.y = minnaMesh.position.y;
        basilMesh.position.z = minnaMesh.position.z;
        // Elder light bumps to sell the merged halo.
        if ((elderLeo as unknown as { glowLight?: { intensity: number } }).glowLight) {
          (elderLeo as unknown as { glowLight: { intensity: number } }).glowLight.intensity = 4.5;
        }
      }},
      { type: "pan", target: mergeTarget, duration: 1200 },
      { type: "wait", duration: 1200 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        // Restore Basil's position in case anything reads it before the
        // scene disposes on level complete.
        if (savedBasilPos) {
          basilMesh.position = savedBasilPos;
        }
        completeLevelSequence();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  function completeLevelSequence(): void {
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "ClockworkCanopy" });
  }

  // ─── Dispose puzzles and Elder on scene teardown ─────────────────

  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // L3 is the finale; 2 caches placed at quiet hand-offs give Basil a
  // breather before the apex orrery puzzle without making the level
  // feel "easy."
  const greaseCachesL3 = [
    new GreaseCache(scene, {
      key: "greaseCache_L3_a",
      position: new Vector3(35, 2.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L3_b",
      position: new Vector3(96, 2.2, 0),
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCachesL3) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  // ─── Game Loop ───────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      // Tick each puzzle and refresh its entry in levelState so the apex
      // puzzle always sees up-to-date solved flags.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick grease caches so respawn timers advance.
      for (const cache of greaseCachesL3) {
        cache.tick(dt);
      }

      // Elder Leo idle bob + glow orbit (only matters once revealed).
      if (elderLeo.mesh.isEnabled()) {
        elderLeo.update(sceneTime, dt);
      }

      // Making Biscuits: tick the minigame + proximity prompt for Leo's sweater.
      leoBiscuit.update(dt);
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active —
        // Minna-only, so the Elder anchor handler doesn't conflict with
        // Basil's Grease Cache on `,`.
        if (leoBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }
        // Wounds/Medicine: check nearby Grease Cache before puzzle keys.
        if (key === "," && pressed) {
          for (const cache of greaseCachesL3) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
        }

        // Give each unsolved puzzle a chance to handle the key. Forced-
        // solved prior puzzles keep their internal meshes around for
        // scenery, but they must not consume apex inputs after
        // `startAtPuzzle` has advanced the level state.
        for (const puzzle of puzzles) {
          if (puzzle.getState().solved) continue;
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }

        // Co-op: F + M = Resonant Drop. Both co-op abilities are unlocked
        // from the start of L3 (see instantiation above). Without these
        // handlers the AbilityInputMapper chord synthesis never reaches
        // the ability, so the TimedResonance puzzle (which gates on
        // RESONANT_DROP_TRIGGERED / CHRONOS_BUBBLE_TRIGGERED events) can
        // never solve. Mirrors the L1 RedwoodScrapyard pattern.
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        // Co-op: G + N = Chronos Bubble
        if (key === "g+n" && pressed) {
          tryCoopAbility(chronosBubble, "chronosBubble");
          return true;
        }

        return false;
      },
    },
  );

  // ─── Start ───────────────────────────────────────────────────────

  setTimeout(() => audioManager.startAmbient(), 500);

  gameEvents.emit(GameEvents.LEVEL_STARTED, "ClockworkCanopy");

  // Opening cutscene
  const introLines = LEVEL_INTRO["ClockworkCanopy"] ?? [];
  const introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 400 },
    ...introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "wait", duration: 300 },
    { type: "dialogue", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    { type: "dialogue", speaker: "", text: "All abilities are available. Resonant Drop: hold F + M together. Chronos Bubble: hold G + N together.", duration: 4000 },
    { type: "wait", duration: 300 },
  ];

  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    cutsceneSystem,
  };
}

// ─── Helper: Background ─────────────────────────────────────────────

function createBackground(scene: Scene): void {
  // Backdrop panels (simulated sky)
  const skyWidth = 180;
  const sky = MeshBuilder.CreatePlane("skyBackdrop", { width: skyWidth, height: 60 }, scene);
  sky.position = new Vector3(skyWidth / 2 - 20, 25, -10);
  const skyMat = new StandardMaterial("skyMat", scene);
  skyMat.diffuseColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.STARLIT_SKY);
  skyMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.MIDNIGHT_BLUE).scale(0.3);
  skyMat.specularColor = Color3.Black();
  skyMat.backFaceCulling = false;
  sky.material = skyMat;
  sky.isPickable = false;
  tagAs(sky, "placeholder");

  // ── Parallax backdrop layers ────────────────────────────────────────
  // Three depth-staggered planes that scroll at fractional rates with
  // the active camera's X position. Addresses a Gemini fluidity review
  // note: L3 background reads as "a flat blue void with no parallax."
  //
  // Layer 0 (far): distant redwood peak silhouettes, z=-9
  // Layer 1 (mid): misty canopy fronds, z=-7
  // Layer 2 (near): foreground canopy ferns, z=-3 (scrolls fastest)
  //
  // Each layer is built from a few tall tapered boxes positioned in
  // a seeded pattern, then registered on a scene before-render hook
  // that offsets their X by a fraction of the camera delta. The
  // illusion of depth comes from different scroll speeds, not shader
  // tricks — it's a classic 2D side-scroller parallax applied to the
  // 2.5D Babylon scene.
  const parallaxLayers: Array<{
    root: TransformNode;
    rate: number;
    baseX: number;
  }> = [];

  const layerSpecs = [
    { name: "canopyFar",  z: -9,  count: 9,  rate: 0.85, tint: "#0c1226", width: 4.0, height: 22, yBase: 22 },
    { name: "canopyMid",  z: -7,  count: 7,  rate: 0.70, tint: "#111d38", width: 5.0, height: 16, yBase: 18 },
    { name: "canopyNear", z: -3,  count: 6,  rate: 0.40, tint: "#0a101c", width: 3.2, height: 10, yBase: 12 },
  ];

  let parallaxSeed = 0x5a5a5a5a | 0;
  const rp = (): number => {
    parallaxSeed = (parallaxSeed * 1664525 + 1013904223) | 0;
    return ((parallaxSeed >>> 0) / 0xffffffff);
  };

  for (const spec of layerSpecs) {
    const root = new TransformNode(`parallax_${spec.name}_root`, scene);
    const mat = new StandardMaterial(`parallax_${spec.name}_mat`, scene);
    mat.diffuseColor = Color3.FromHexString(spec.tint);
    mat.emissiveColor = Color3.FromHexString(spec.tint).scale(0.3);
    mat.specularColor = Color3.Black();
    mat.backFaceCulling = false;
    for (let i = 0; i < spec.count; i++) {
      const x = (i / spec.count) * skyWidth + (rp() - 0.5) * 6;
      const hScale = 0.7 + rp() * 0.8;
      const box = MeshBuilder.CreateBox(
        `${spec.name}_${i}`,
        {
          width: spec.width + rp() * 1.5,
          height: spec.height * hScale,
          depth: 0.6,
        },
        scene,
      );
      box.position = new Vector3(x, spec.yBase * hScale * 0.5, 0);
      box.material = mat;
      box.parent = root;
      box.isPickable = false;
      tagAs(box, "placeholder");
    }
    root.position = new Vector3(0, 0, spec.z);
    parallaxLayers.push({ root, rate: spec.rate, baseX: 0 });
  }

  // Scroll hook. Each layer moves by `(1 - rate) * camera.x` relative
  // to its baseX, so far layers (rate high) barely move and near
  // layers (rate low) track the camera more closely. The net effect:
  // moving right causes far peaks to drift left slowly while near
  // ferns drift left faster.
  scene.onBeforeRenderObservable.add(() => {
    const cam = scene.activeCamera;
    if (!cam) return;
    for (const layer of parallaxLayers) {
      layer.root.position.x = cam.position.x * (1 - layer.rate);
    }
  });

  // ── Procedural starfield ────────────────────────────────────────────
  // Hundreds of tiny emissive disc-stars scattered across the sky plane.
  // Deterministic via a seeded LCG so two runs of the harness produce
  // an identical snapshot hash. Tagged placeholder so the asset audit
  // knows these are stubs until a proper star texture lands.
  const starMat = new StandardMaterial("starMat", scene);
  starMat.diffuseColor = Color3.Black();
  starMat.specularColor = Color3.Black();
  // Full-brightness emissive so DefaultRenderingPipeline's bloom actually
  // blooms them. Stars that read dim in the shipped screenshot are being
  // clamped back down by tonemapping; giving them a 5+ multiplier puts
  // them above the bloom threshold and they pop.
  starMat.emissiveColor = new Color3(6, 6, 7);
  starMat.disableLighting = true;
  const starMatWarm = new StandardMaterial("starMatWarm", scene);
  starMatWarm.diffuseColor = Color3.Black();
  starMatWarm.specularColor = Color3.Black();
  starMatWarm.emissiveColor = new Color3(7, 5, 3);
  starMatWarm.disableLighting = true;
  let seed = 0x9e3779b9 | 0;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) | 0;
    return ((seed >>> 0) / 0xffffffff);
  };
  const starCount = 150;
  for (let i = 0; i < starCount; i++) {
    const x = -10 + rand() * (skyWidth + 10);
    // Visible y at the spawn camera frustum is roughly [-2..10] (camera
    // y=4 ± ~6 at z=-5 through a 60° FOV). Cluster stars from y=4..28
    // so some are visible at every camera altitude as cats ascend.
    const y = 4 + rand() * 24;
    const size = 0.55 + Math.pow(rand(), 2) * 0.9;
    const star = MeshBuilder.CreatePlane(`star_${i}`, { width: size, height: size }, scene);
    star.position = new Vector3(x, y, -5);
    star.material = i % 7 === 0 ? starMatWarm : starMat;
    star.isPickable = false;
    tagAs(star, "placeholder");
  }

  // ── Constellation lines ─────────────────────────────────────────────
  // Thin emissive line segments between adjacent constellation point-
  // light positions, so the orrery's "star charts" actually read as
  // charted constellations rather than scattered dots.
  const constellationNodes: Vector3[] = [
    new Vector3(5, 9, -5.0), new Vector3(18, 12, -5.0),
    new Vector3(32, 8, -5.0), new Vector3(48, 14, -5.0),
    new Vector3(66, 10, -5.0), new Vector3(82, 13, -5.0),
    new Vector3(100, 9, -5.0), new Vector3(118, 12, -5.0),
    new Vector3(136, 10, -5.0),
  ];
  const lineMat = new StandardMaterial("constellationLineMat", scene);
  lineMat.diffuseColor = Color3.Black();
  lineMat.specularColor = Color3.Black();
  lineMat.emissiveColor = new Color3(2.0, 2.2, 3.0);
  lineMat.disableLighting = true;
  for (let i = 0; i < constellationNodes.length - 1; i++) {
    const a = constellationNodes[i];
    const b = constellationNodes[i + 1];
    const mid = a.add(b).scale(0.5);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    const seg = MeshBuilder.CreateBox(`constellationLine_${i}`, { width: len, height: 0.12, depth: 0.04 }, scene);
    seg.position = mid;
    seg.rotation.z = angle;
    seg.material = lineMat;
    seg.isPickable = false;
    tagAs(seg, "placeholder");
  }

  // Redwood tree trunks (tall dark silhouettes)
  const trunkPositions = [
    new Vector3(-5, 15, -3), new Vector3(25, 18, -4),
    new Vector3(55, 16, -3), new Vector3(85, 17, -4),
    new Vector3(115, 19, -3), new Vector3(140, 16, -4),
  ];
  const trunkMat = new PBRMaterial("trunkMat", scene);
  trunkMat.albedoColor = new Color3(0.15, 0.08, 0.05);
  trunkMat.metallic = 0.0;
  trunkMat.roughness = 0.95;

  for (let i = 0; i < trunkPositions.length; i++) {
    const trunk = MeshBuilder.CreateBox(`trunk_${i}`, { width: 3, height: 40, depth: 2 }, scene);
    trunk.position = trunkPositions[i];
    trunk.material = trunkMat;
    trunk.isPickable = false;
    tagAs(trunk, "placeholder");
  }
}

// ─── Helper: Starfield (constellation patterns) ─────────────────────

function createStarfield(scene: Scene): void {
  // Small emissive spheres scattered across the sky backdrop
  const starMat = new StandardMaterial("starMat", scene);
  starMat.diffuseColor = Color3.Black();
  starMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION);
  starMat.specularColor = Color3.Black();

  const starPositions: Vector3[] = [];
  // Procedural star placement
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * 160 - 10;
    const y = 20 + Math.random() * 25;
    const z = -8 - Math.random() * 3;
    starPositions.push(new Vector3(x, y, z));
  }

  for (let i = 0; i < starPositions.length; i++) {
    const size = 0.05 + Math.random() * 0.15;
    const star = MeshBuilder.CreateSphere(`star_${i}`, { diameter: size, segments: 4 }, scene);
    star.position = starPositions[i];
    star.material = starMat;
    star.isPickable = false;
    tagAs(star, "placeholder");
  }

  // Constellation lines (thin boxes connecting stars)
  const lineMat = new StandardMaterial("constellationLineMat", scene);
  lineMat.diffuseColor = Color3.Black();
  lineMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION).scale(0.3);
  lineMat.alpha = 0.4;

  // A few hand-placed constellation segments
  const constellationSegments = [
    { from: new Vector3(30, 28, -9), to: new Vector3(38, 32, -9) },
    { from: new Vector3(38, 32, -9), to: new Vector3(45, 29, -9) },
    { from: new Vector3(80, 30, -9), to: new Vector3(88, 34, -9) },
    { from: new Vector3(88, 34, -9), to: new Vector3(95, 31, -9) },
    { from: new Vector3(95, 31, -9), to: new Vector3(90, 27, -9) },
  ];

  for (let i = 0; i < constellationSegments.length; i++) {
    const seg = constellationSegments[i];
    const mid = seg.from.add(seg.to).scale(0.5);
    const len = Vector3.Distance(seg.from, seg.to);
    const line = MeshBuilder.CreateBox(`constLine_${i}`, { width: len, height: 0.03, depth: 0.03 }, scene);
    line.position = mid;
    // Rotate to face from -> to
    const dir = seg.to.subtract(seg.from).normalize();
    line.rotation.z = Math.atan2(dir.y, dir.x);
    line.material = lineMat;
    line.isPickable = false;
    tagAs(line, "placeholder");
  }
}

// ─── Helper: Ground and Platforms ───────────────────────────────────

function createGroundAndPlatforms(
  scene: Scene,
  physics: PhysicsSystem,
  shadowGen: ShadowGenerator,
): Mesh[] {
  const platforms: Mesh[] = [];

  // Ground plane
  const ground = physics.createGround(
    CLOCKWORK_CANOPY.WORLD_WIDTH,
    CLOCKWORK_CANOPY.GROUND_DEPTH,
    CLOCKWORK_CANOPY.WORLD_WIDTH / 2,
  );
  const groundMat = new PBRMaterial("groundMat", scene);
  groundMat.albedoColor = new Color3(0.1, 0.07, 0.05);
  groundMat.metallic = 0.1;
  groundMat.roughness = 0.9;
  groundMat.emissiveColor = new Color3(0.005, 0.003, 0.008);
  if (ground) {
    ground.material = groundMat;
    ground.receiveShadows = true;
  }

  // Platform material: weathered brass.
  // Emissive bumped from near-zero so the platforms actually read
  // against the dark starlit scene. Gemini 3.1-pro flagged `plat_s1_4`
  // near gear B as "invisible / Basil floats in mid-air" under the
  // prior 0.01-channel emissive — the brass needs enough internal
  // glow to survive the deep vignette + high bloom threshold of the
  // CINEMATIC_NIGHT post-process preset. Bump also gives the plank
  // surfaces variation under the pulsing brass-orrery point light.
  const platMat = new PBRMaterial("platMat", scene);
  platMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.COPPER_DARK);
  platMat.metallic = 0.5;
  platMat.roughness = 0.6;
  platMat.emissiveColor = new Color3(0.08, 0.05, 0.02);
  const canopyPlatNoise = getOrMakeNoiseBump(scene, "canopy_platform_brass", 0.4);
  canopyPlatNoise.uScale = 6;
  canopyPlatNoise.vScale = 1;
  platMat.bumpTexture = canopyPlatNoise;
  platMat.bumpTexture.level = 0.25;
  const platformTrimMat = new PBRMaterial("canopyPlatformTrimMat", scene);
  platformTrimMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS).scale(0.85);
  platformTrimMat.metallic = 0.75;
  platformTrimMat.roughness = 0.34;
  platformTrimMat.emissiveColor = new Color3(0.08, 0.055, 0.025);
  const platformRivetMat = new PBRMaterial("canopyPlatformRivetMat", scene);
  platformRivetMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM).scale(0.75);
  platformRivetMat.metallic = 0.85;
  platformRivetMat.roughness = 0.28;
  platformRivetMat.emissiveColor = new Color3(0.12, 0.08, 0.025);

  // Multi-tier platform network — geometry lives in
  // src-babylon/levels/layouts/ so the reachability lint can check it.
  const platDefs = CLOCKWORK_CANOPY_PLATFORMS.platforms.map((def) => ({
    name: def.name,
    w: def.w,
    h: platformHeight(def),
    d: platformDepth(def),
    pos: new Vector3(def.x, def.y, 0),
  }));

  for (const def of platDefs) {
    const plat = physics.createPlatform(def.name, def.w, def.h, def.d, def.pos);
    plat.material = platMat;
    plat.receiveShadows = true;
    shadowGen.addShadowCaster(plat);
    platforms.push(plat);

    const frontTrim = MeshBuilder.CreateBox(
      `${def.name}_frontBrassTrim`,
      { width: def.w * 0.96, height: 0.08, depth: 0.08 },
      scene,
    );
    frontTrim.position.set(def.pos.x, def.pos.y + def.h / 2 + 0.09, def.pos.z + def.d / 2 + 0.05);
    frontTrim.material = platformTrimMat;
    frontTrim.receiveShadows = true;
    frontTrim.isPickable = false;
    tagAs(frontTrim, "modular-final");

    const backTrim = MeshBuilder.CreateBox(
      `${def.name}_backBrassTrim`,
      { width: def.w * 0.9, height: 0.05, depth: 0.05 },
      scene,
    );
    backTrim.position.set(def.pos.x, def.pos.y + def.h / 2 + 0.05, def.pos.z - def.d / 2 - 0.04);
    backTrim.material = platformTrimMat;
    backTrim.receiveShadows = true;
    backTrim.isPickable = false;
    tagAs(backTrim, "modular-final");

    for (const side of [-1, 1]) {
      const rivet = MeshBuilder.CreateCylinder(
        `${def.name}_trimRivet_${side > 0 ? "e" : "w"}`,
        { height: 0.08, diameter: 0.18, tessellation: 12 },
        scene,
      );
      rivet.position.set(
        def.pos.x + side * Math.max(def.w / 2 - 0.35, 0.15),
        def.pos.y + def.h / 2 + 0.16,
        def.pos.z + def.d / 2 + 0.09,
      );
      rivet.material = platformRivetMat;
      rivet.receiveShadows = true;
      rivet.isPickable = false;
      tagAs(rivet, "modular-final");
    }
  }

  // Central orrery dais. Keep it inside the gap between the sync
  // platforms so it reads as a brass focal point without becoming a
  // low collision ceiling over the approach route.
  const dais = CLOCKWORK_CANOPY_PLATFORMS.extraColliders?.find((d) => d.name === "orreryPlatform");
  if (!dais) throw new Error("ClockworkCanopy layout is missing orreryPlatform");
  const orreryPlat = physics.createPlatform(
    dais.name,
    dais.w,
    platformHeight(dais),
    platformDepth(dais),
    new Vector3(dais.x, dais.y, 0),
  );
  const orreryPlatMat = new PBRMaterial("orreryPlatMat", scene);
  orreryPlatMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS).scale(0.7);
  orreryPlatMat.metallic = 0.8;
  orreryPlatMat.roughness = 0.3;
  // C6: brass-hammered bump so the glancing highlights break up nicely
  // under the new orrery brass glow light (see C5).
  const orreryNoise = getOrMakeNoiseBump(scene, "canopy_orrery_brass", 0.4);
  orreryNoise.uScale = 2.2;
  orreryNoise.vScale = 1;
  orreryPlatMat.bumpTexture = orreryNoise;
  orreryPlatMat.bumpTexture.level = 0.25;
  orreryPlat.material = orreryPlatMat;
  orreryPlat.receiveShadows = true;
  shadowGen.addShadowCaster(orreryPlat);
  platforms.push(orreryPlat);

  return platforms;
}

// ─── Helper: Shadow Zones ───────────────────────────────────────────

function createShadowZones(scene: Scene): ShadowZoneInfo[] {
  const zones: ShadowZoneInfo[] = [];

  const zoneDefs = [
    // Under platforms in section 1
    { position: new Vector3(10, 1.2, 0), width: 5, height: 1.0, depth: 1.5 },
    // Shadow relay corridor (section 4). Height/center chosen so the AABB
    // spans y∈[0.5, 3.5]: Minna's capsule center sits at ~1.0 on the
    // ground route and ~2.9 while walking plat_s4_2 (top y=2.2). The old
    // 1.5-tall zones ended at y=2.25 — BELOW the platform walk path — so
    // Shadow Slip never engaged on the route the puzzle actually asks
    // Minna to take past the fox.
    // `veil: true` — the relay corridor renders its soft dark veil in
    // normal play (not just debug). The fox here now has real teeth, so
    // the safe-stealth region must be readable to the player, matching
    // the "phantom geometry must be visible" standard set by the L2
    // vine bridges and nest columns.
    { position: new Vector3(92, 2.0, 0), width: 8, height: 3.0, depth: 1.5, veil: true },
    { position: new Vector3(100, 2.0, 0), width: 6, height: 3.0, depth: 1.5, veil: true },
    // Under orrery platform (ground route: cat capsule center ~1.0).
    { position: new Vector3(125, 1.5, 0), width: 10, height: 3.0, depth: 1.5 },
  ] as Array<{ position: Vector3; width: number; height: number; depth: number; veil?: boolean }>;

  // Shared veil dressing (Q-023 remainder): the relay-corridor veils
  // read as flat QA boxes. They are now gothic shade cloth — a deep
  // violet jewel tone with a scalloped, bottom-fading opacity gradient
  // and a brass hanging rod along the top edge. Both shared resources
  // are created lazily on the first veil zone; scene disposal owns
  // their lifetime (the zones live for the whole level).
  //
  // NOTE: this is a LIT StandardMaterial + opacityTexture — no
  // disableLighting — deliberately avoiding the ParallaxBackground
  // black-cutout pitfall.
  let veilClothTex: DynamicTexture | null = null;
  let veilRodMat: StandardMaterial | null = null;
  const getVeilClothTex = (): DynamicTexture => {
    if (veilClothTex) return veilClothTex;
    const texW = 128;
    const texH = 64;
    veilClothTex = new DynamicTexture(
      "shadowVeilClothTex",
      { width: texW, height: texH },
      scene,
      false,
    );
    const ctx = veilClothTex.getContext() as unknown as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, texW, texH);
    // Vertical opacity gradient: opaque top → transparent bottom.
    const g = ctx.createLinearGradient(0, 0, 0, texH);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.65, "rgba(255,255,255,0.8)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    // Fill down to a 6-scallop bottom hem so the cloth reads tailored,
    // not box-cut.
    const scallops = 6;
    const hemY = texH * 0.72;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(texW, 0);
    ctx.lineTo(texW, hemY);
    const scallopW = texW / scallops;
    for (let sIdx = scallops - 1; sIdx >= 0; sIdx--) {
      const xEnd = sIdx * scallopW;
      ctx.quadraticCurveTo(xEnd + scallopW / 2, texH, xEnd, hemY);
    }
    ctx.closePath();
    ctx.fill();
    veilClothTex.update(false);
    veilClothTex.hasAlpha = true;
    return veilClothTex;
  };
  const getVeilRodMat = (): StandardMaterial => {
    if (veilRodMat) return veilRodMat;
    veilRodMat = new StandardMaterial("shadowVeilRodMat", scene);
    veilRodMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    // ≤0.1 emissive so the GlowLayer doesn't beacon the rod (Q-037).
    veilRodMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.1);
    veilRodMat.specularColor = Color3.Black();
    return veilRodMat;
  };

  for (let i = 0; i < zoneDefs.length; i++) {
    const def = zoneDefs[i];
    // Detection mesh — consumed by ShadowSlip via `zones.push({ mesh })`.
    // MUST stay untouched (name, bounds, invisibility).
    const mesh = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    mesh.position = def.position;
    mesh.isVisible = false;
    mesh.isPickable = false;

    // Visual: gothic shade cloth for player-facing veils; flat debug box
    // (behind WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS) for the rest.
    const debugVisual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    debugVisual.position = def.position;
    debugVisual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS || def.veil === true;
    debugVisual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    if (def.veil === true) {
      // Deep-violet jewel tone per the art direction, with the scalloped
      // fade so the cloth dissolves toward the floor.
      darkMat.diffuseColor = new Color3(0.09, 0.05, 0.16);
      darkMat.emissiveColor = new Color3(0.025, 0.012, 0.05);
      darkMat.alpha = 0.45;
      darkMat.opacityTexture = getVeilClothTex();

      // Brass hanging rod along the veil's top edge.
      const rod = MeshBuilder.CreateCylinder(
        `shadowZoneVeilRod_${i}`,
        { diameter: 0.1, height: def.width },
        scene,
      );
      rod.rotation.z = Math.PI / 2;
      rod.position = new Vector3(
        def.position.x,
        def.position.y + def.height / 2 + 0.05,
        0,
      );
      rod.material = getVeilRodMat();
      rod.isPickable = false;
      rod.receiveShadows = false;
      // NOT added to any ShadowGenerator (caster-trim rule, cd1202f).
    } else {
      darkMat.diffuseColor = new Color3(0.01, 0.005, 0.03);
      darkMat.emissiveColor = new Color3(0.005, 0.003, 0.015);
      darkMat.alpha = 0.35;
    }
    debugVisual.material = darkMat;

    zones.push({ mesh });
  }

  return zones;
}
